import React, { useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

const METRIC_CONFIG = {
  tripsProcessed: {
    label: 'Trips Processed',
    color: '#07A95B', // Emerald green
    yAxisId: 'right',
  },
  totalVehicles: {
    label: 'Total Vehicles',
    color: '#2563EB', // Royal blue
    yAxisId: 'left',
  },
  totalDues: {
    label: 'Total Dues',
    color: '#F59E0B', // Amber orange
    yAxisId: 'left',
  },
  totalPayouts: {
    label: 'Total Payouts',
    color: '#8B5CF6', // Purple
    yAxisId: 'left',
  },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.06)',
          minWidth: '180px',
        }}
      >
        <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 700, color: '#111827' }}>
          {label}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {payload.map((item) => {
            const config = METRIC_CONFIG[item.dataKey] || {};
            return (
              <div
                key={item.dataKey}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  fontSize: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ color: '#4B5563', fontWeight: 500 }}>
                    {config.label || item.name}
                  </span>
                </div>
                <span style={{ fontWeight: 700, color: '#111827' }}>
                  {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

const PerformanceTrendsChart = ({ data = [], loading = false }) => {
  const [visibleLines, setVisibleLines] = useState({
    tripsProcessed: true,
    totalVehicles: true,
    totalDues: true,
    totalPayouts: true,
  });

  const toggleLine = (key) => {
    setVisibleLines((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const chartData = Array.isArray(data) && data.length > 0 ? data : [];

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        padding: '24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* Header with Title & Legend Toggles */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
            Performance & Operations Trends
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6B7280' }}>
            Monthly comparison across vehicles, trips processed, payouts, and dues
          </p>
        </div>

        {/* Interactive Clickable Badges to Toggle Lines */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {Object.entries(METRIC_CONFIG).map(([key, config]) => {
            const isVisible = visibleLines[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleLine(key)}
                style={{
                  border: isVisible ? `1px solid ${config.color}` : '1px solid #E5E7EB',
                  backgroundColor: isVisible ? `${config.color}14` : '#F9FAFB',
                  color: isVisible ? config.color : '#9CA3AF',
                  padding: '5px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                  opacity: isVisible ? 1 : 0.6,
                }}
                title={`Click to ${isVisible ? 'hide' : 'show'} ${config.label}`}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: isVisible ? config.color : '#D1D5DB',
                  }}
                />
                <span>{config.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart Canvas */}
      <div style={{ width: '100%', height: '300px', position: 'relative' }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              fontSize: '14px',
            }}
          >
            Loading trend data...
          </div>
        ) : chartData.length === 0 ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              fontSize: '14px',
            }}
          >
            No trend data available for this range
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
              <XAxis
                dataKey="month"
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 12, fontWeight: 500 }}
                dy={8}
              />
              {/* Left Y Axis for Counts (Vehicles, Dues, Payouts) */}
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 11 }}
                allowDecimals={false}
              />
              {/* Right Y Axis for Trips Processed */}
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#07A95B', fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />

              {visibleLines.tripsProcessed && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="tripsProcessed"
                  name="Trips Processed"
                  stroke={METRIC_CONFIG.tripsProcessed.color}
                  strokeWidth={3}
                  dot={{ r: 4, stroke: METRIC_CONFIG.tripsProcessed.color, strokeWidth: 2, fill: '#FFFFFF' }}
                  activeDot={{ r: 7, stroke: '#FFFFFF', strokeWidth: 2, fill: METRIC_CONFIG.tripsProcessed.color }}
                />
              )}

              {visibleLines.totalVehicles && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="totalVehicles"
                  name="Total Vehicles"
                  stroke={METRIC_CONFIG.totalVehicles.color}
                  strokeWidth={3}
                  dot={{ r: 4, stroke: METRIC_CONFIG.totalVehicles.color, strokeWidth: 2, fill: '#FFFFFF' }}
                  activeDot={{ r: 7, stroke: '#FFFFFF', strokeWidth: 2, fill: METRIC_CONFIG.totalVehicles.color }}
                />
              )}

              {visibleLines.totalDues && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="totalDues"
                  name="Total Dues"
                  stroke={METRIC_CONFIG.totalDues.color}
                  strokeWidth={2.5}
                  dot={{ r: 4, stroke: METRIC_CONFIG.totalDues.color, strokeWidth: 2, fill: '#FFFFFF' }}
                  activeDot={{ r: 7, stroke: '#FFFFFF', strokeWidth: 2, fill: METRIC_CONFIG.totalDues.color }}
                />
              )}

              {visibleLines.totalPayouts && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="totalPayouts"
                  name="Total Payouts"
                  stroke={METRIC_CONFIG.totalPayouts.color}
                  strokeWidth={2.5}
                  dot={{ r: 4, stroke: METRIC_CONFIG.totalPayouts.color, strokeWidth: 2, fill: '#FFFFFF' }}
                  activeDot={{ r: 7, stroke: '#FFFFFF', strokeWidth: 2, fill: METRIC_CONFIG.totalPayouts.color }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default PerformanceTrendsChart;
